import { createGlobalEmitter } from '@solid-primitives/event-bus';

import { SensorListener } from './sensorListener';

const isClient = !import.meta.env.SSR;

let emitterInstance: ReturnType<typeof createGlobalEmitter<TrackEvents>> | null = null;

function getGlobalTrackEmitter() {
  if (!emitterInstance) {
    createRoot(() => {
      emitterInstance = createGlobalEmitter<TrackEvents>();
    });
  }
  return emitterInstance!;
}

/** 跟踪系统事件名 */
enum TrackEventName {
  /** 初始化 */
  INIT = 'init',
  /** 落地页重定向 */
  RETARGETING = 'retargeting',
  /** 开始注册 */
  REGISTRATION_START = 'registration_start',
  /** 注册失败 */
  REGISTRATION_FAIL = 'registration_fail',
  /** 人机验证失败 */
  REGISTRATION_CAPTCHA_FAIL = 'registration_captcha_fail',
  /** 注册成功 */
  REGIST_SUCCESS = 'regist_success',
  /** 放弃注册 */
  REGISTRATION_ABANDONED = 'registration_abandoned',
  /** 第三方注册 */
  THIRD_REGISTER = 'third_register',
  /** 第三方登录成功 */
  THIRD_LOGIN_SUCCESS = 'third_login_success',
  /** 复充（不含首充） */
  RECURRING_DEPOSIT = 'reccuring_deposit',
  /** 登录成功 */
  LOGIN_SUCCESS = 'login_success',
  /** 进入充值页面 */
  DEPOSIT_PAGE = 'deposit_page',
  /** 充值开始 */
  DEPOSIT_INITIATION = 'deposit_initiation',
  /** 打开充值窗口 */
  DEPOSIT_OPEN_WINDOW = 'deposit_open_window',
  /** 关闭充值窗口 */
  DEPOSIT_WINDOW_CLOSED = 'deposit_window_closed',
  /** 加密货币充值开始 */
  DEPOSIT_CPT_INITIATION = 'deposit_cpt_initiation',
  /** 首充成功 */
  FTD_SUCCESS = 'ftd_success',
  /** 充值成功（含首充） */
  DEPOSIT_SUCCESS = 'deposit_success',
  /** 访问首页 */
  HOME_PAGE = 'home_page',
  /** 访问注册页 */
  REGIST_PAGE = 'regist_page',
  /** 访问登录页 */
  LOGIN_PAGE = 'login_page',
  /** BuyCrypto 金额设置 */
  BUY_CRYPTO_AMOUNT = 'buy_crypto_amount',
  /** Rakeback 解锁 */
  RAKEBACK_UNLOCK = 'rakeback_unlock',
  /** 打开 LiveSupport */
  ASSISTANCE_REQUESTED = 'assistance_requested',
  /** 登出 */
  LOGOUT = 'logout',
  /** 解析参数 */
  PARSE_PARAMS = 'parseParams',
  /** 其它事件 */
  OTHER_EVENTS = 'other_events',
  /** 页面浏览 */
  PAGE_VIEW = 'page_view',
  /** 神策用户信息绑定 */
  PROFILE_SET = 'profile_set',
  /** 神策自定义事件 */
  SENSORS_TRACK = 'sensors_track',
  /** 测试事件 */
  TEST = 'testtesttest',
  /** 进入注册页面 */
  BZ_PAGE_VIEW = 'bz_page_view',
  /** 注册按钮被点击 */
  BZ_REGISTER_PAGE_EXPOSE = 'bz_register_page_expose',
  /** 注册成功事件 */
  BZ_REGISTER_SUCCESS = 'bz_register_success',
  /** 侧边栏按钮点击 */
  BZ_SIDEBAR_BUTTON_CLICK = 'bz_sidebar_button_click',
  /** 比赛曝光事件 */
  BZ_MATCH_EXPOSE = 'bz_match_expose',
  /** 比赛点击事件 */
  BZ_MATCH_CLICK = 'bz_match_click',
}

/** 跟踪系统事件类型 */
interface TrackEvents {
  [TrackEventName.INIT]: void;
  [TrackEventName.RETARGETING]: void;
  [TrackEventName.REGISTRATION_START]: { sign_type: string };
  [TrackEventName.REGISTRATION_FAIL]: { sign_type: string; error: any };
  [TrackEventName.REGISTRATION_CAPTCHA_FAIL]: void;
  [TrackEventName.REGIST_SUCCESS]: {
    userId: string | number;
    level?: string | number;
  };
  [TrackEventName.REGISTRATION_ABANDONED]: void;
  [TrackEventName.THIRD_REGISTER]: { account_type: string };
  [TrackEventName.THIRD_LOGIN_SUCCESS]: {
    userId: string | number;
    account_type: string;
  };
  [TrackEventName.RECURRING_DEPOSIT]: {
    sales: string | number;
    orderid: string | number;
    userId: string | number;
  };
  [TrackEventName.LOGIN_SUCCESS]: { userId: string | number };
  [TrackEventName.DEPOSIT_PAGE]: void;
  [TrackEventName.DEPOSIT_INITIATION]: {
    amount: string | number;
    currency: string;
    payment_method: string;
  };
  [TrackEventName.DEPOSIT_OPEN_WINDOW]: {
    url: string;
    timestamp: number;
    extras?: any;
  };
  [TrackEventName.DEPOSIT_WINDOW_CLOSED]: {
    url: string;
    timestamp: number;
    duration: number;
    extras?: any;
  };
  [TrackEventName.DEPOSIT_CPT_INITIATION]: {
    currency: string;
    chain: string;
  };
  [TrackEventName.FTD_SUCCESS]: {
    sales: string | number;
    orderid: string | number;
    userId: string | number;
  };
  [TrackEventName.DEPOSIT_SUCCESS]: {
    sales: string | number;
    orderid: string | number;
    userId: string | number;
  };
  [TrackEventName.HOME_PAGE]: void;
  [TrackEventName.REGIST_PAGE]: void;
  [TrackEventName.LOGIN_PAGE]: void;
  [TrackEventName.BUY_CRYPTO_AMOUNT]: void;
  [TrackEventName.RAKEBACK_UNLOCK]: number;
  [TrackEventName.ASSISTANCE_REQUESTED]: void;
  [TrackEventName.LOGOUT]: void;
  [TrackEventName.PARSE_PARAMS]: string;
  [TrackEventName.OTHER_EVENTS]: { e: string; p?: any };
  [TrackEventName.PAGE_VIEW]: {
    url: string;
    referrer?: string;
    title?: string;
  };
  [TrackEventName.PROFILE_SET]: {
    [key: string]: any;
  };
  [TrackEventName.SENSORS_TRACK]: { event: string; [key: string]: any };
  [TrackEventName.TEST]: string;
  [TrackEventName.BZ_PAGE_VIEW]: { [key: string]: any };
  [TrackEventName.BZ_REGISTER_PAGE_EXPOSE]: { [key: string]: any };
  [TrackEventName.BZ_REGISTER_SUCCESS]: { [key: string]: any };
  [TrackEventName.BZ_SIDEBAR_BUTTON_CLICK]: { [key: string]: any };
  [TrackEventName.BZ_MATCH_EXPOSE]: { [key: string]: any };
  [TrackEventName.BZ_MATCH_CLICK]: { [key: string]: any };
}
// Create global event emitter and destructure its methods
const globalTrackEmitter = getGlobalTrackEmitter();
const { on, emit, clear, listen } = globalTrackEmitter;

/** 用户事件管理器 */
class TrackEventBus {
  initParams = {
    ch: '',
    pixel_id: '',
  };

  constructor() {
    this.setupParseParamsListener();
    this.initializeTracking();
  }

  private setupParseParamsListener() {
    on(TrackEventName.PARSE_PARAMS, (url) => {
      if (!isClient) return;
      /** 待定，还没有整体的邀请和推广逻辑 **/
      try {
        const params = new URLSearchParams(url.split('?')[1]);
        const initParamsNS = 'pixel_info';
        const initSearchParams = new URLSearchParams();

        const pms = {
          ch: initSearchParams.get('ch') || params.get('ch'),
          pixel_id:
            initSearchParams.get('pixel_id') ||
            initSearchParams.get('pixelid') ||
            params.get('pixel_id') ||
            params.get('pixelid'),
        };

        if (pms.ch || pms.pixel_id) {
          window.localStorage.setItem(initParamsNS, JSON.stringify(pms));
        }

        const local = JSON.parse(window.localStorage.getItem(initParamsNS) || '{}');
        Object.assign(this.initParams, local);
      } catch (_err) {
        console.error(_err);
        // Silently handle errors
      }
    });
  }

  private initializeTracking() {
    if (!isClient) return;
    // Emit init after 1 seconds
    setTimeout(() => {
      emit(TrackEventName.INIT);
    }, 1000);

    // Parse current URL parameters
    emit(TrackEventName.PARSE_PARAMS, window.location?.href || '');

    // Sensors 的 dom 节点事件
    SensorListener();
  }

  /** 安全的事件监听，不会阻塞主流程，有错误处理 */
  safeOn<K extends keyof TrackEvents>(
    event: K,
    listener: TrackEvents[K] extends void ? () => void : (params: TrackEvents[K]) => void
  ) {
    return on(event, (params: any) => {
      // 使用 setTimeout 创建新的执行栈，避免阻塞主流程
      setTimeout(() => {
        try {
          listener(params);
        } catch (_error) {
          console.error(_error);
          // 错误处理不影响主流程
        }
      }, 0);
    });
  }

  // 暴露 event-bus 的方法
  emit = emit;
  on = on;
  clear = clear;

  /** 可以监听所有的事件 */
  listen(callback: (event: { name: keyof TrackEvents; details: any }) => void) {
    return listen(callback);
  }
}

/** 全局跟踪事件管理器 */
const TrackEvent = new TrackEventBus();

export { TrackEvent, TrackEventName };
export type TrackEventParams<T extends keyof TrackEvents> = TrackEvents[T];
